// Global Imports
// import Vue from 'vue'
import { createApp, h } from 'vue'
import { mapGetters } from 'vuex'

import VueResize from 'vue3-resize'
import VueScrollTo from 'vue-scrollto'
import Debug from 'logdown'

// Original App Imports
import { RecycleScroller } from 'vue-virtual-scroller'

// Mixins | Filters
import { loadView, loadComponent } from '@/helpers/asyncLoaders'
import checkUserPermissions from '@/mixins/checkUserPermissions'

// Helpers
import customValidators from '@/helpers/customValidators.js'

// Plugins
import Vuetify from '@/plugins/vuetify'
import i18next from '@/plugins/i18nextParser'

// Directives
import { MenuClose, Uppercase } from '@/directives'

// Libraries
import router from '@/router'
import store from '@/store/store'
import { sync } from 'vuex-router-sync'
import { vMaska } from 'maska/vue'

// emitter library
import mitt from 'mitt'

// AGGrid libs
import { ModuleRegistry, AllCommunityModule, provideGlobalGridOptions } from 'ag-grid-community'
import { LicenseManager, AllEnterpriseModule } from 'ag-grid-enterprise'

// Main app import
import App from '@/App.vue'

// Import actions
import { VUEX_APP_INIT } from '@/store/constants/app'

import { VUEX_LOCALIZATION_CHANGE_LANG } from '@/store/constants/localization'

import { VUEX_TOAST_SHOW } from '@/store/constants/ui/toast'

import AutoComplete from '@/components/_core/Common/AutoComplete.vue'
import ComboBox from '@/components/_core/Common/ComboBox.vue'
import DatePicker from '@/components/_core/Common/DatePicker.vue'
import Select from '@/components/_core/Common/Select.vue'
import TextArea from '@/components/_core/Common/TextArea.vue'
import TextField from '@/components/_core/Common/TextField.vue'

import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'

// Function to determine environment based on domain
const getEnvironmentFromDomain = () => {
  const hostname = window.location.hostname

  if (hostname === 'itsthes.com') {
    return 'production'
  }

  if (hostname.includes('.tbfdev.com')) {
    const subdomain = hostname.split('.')[0]
    if (subdomain.startsWith('its-')) {
      const env = subdomain.replace('its-', '')
      return env === 'staging' ? 'release' : env
    }
  }

  return 'dev' // fallback for localhost or other domains
}

const env = getEnvironmentFromDomain()

datadogRum.init({
  applicationId: '4ce28ad6-ff1f-42be-a4d8-afd99c47c663',
  clientToken: 'pub0dc329d5cbe45720db6c6d0876039b78',
  site: 'datadoghq.com',
  service: 'its-fe',
  env,
  trackResources: true,
  trackLongTasks: true,
  trackUserInteractions: true,
  allowedTracingUrls: [/https:\/\/itsthes\.com/, /^https:\/\/[^\/]+\.tbfdev\.com/],
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  startSessionReplayRecordingManually: true,
  defaultPrivacyLevel: 'allow'
})

datadogLogs.init({
  clientToken: 'pub0dc329d5cbe45720db6c6d0876039b78',
  site: 'datadoghq.com',
  service: 'its-fe',
  env,
  forwardErrorsToLogs: true,
  sessionSampleRate: 100
})

router.onError((error) => {
  if (/loading chunk [\w-]+ failed/i.test(error.message) || /CHUNK_LOAD_FAILED/i.test(error.code)) {
    store.dispatch(VUEX_TOAST_SHOW, {
      component: '_core/Toast/Toast_Message.vue',
      data: {
        type: 'error',
        message: `Warning: Please disable ad blockers or whitelist Its the S to use this site`
      }
    })
  }
})

require('@/utils/navigation_controller')
// require('@/config/vue-config')

const debug = new Debug('its:index')

// Instantiate/Register library instances
LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-075235}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{The_Branding_Farm}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Its_The_S}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Its_The_S}_need_to_be_licensed___{Its_The_S}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{18_March_2026}____[v3]_[01]_MTc3Mzc5MjAwMDAwMA==e06d6d72c6bf66a564b45f7c726f4e9c'
)
ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule])
// mark this as legacy for now - this is going to be a pain:https://www.ag-grid.com/vue-data-grid/theming-migration/
provideGlobalGridOptions({
  theme: 'legacy'
})

sync(store, router)

let app = createApp({
  // vuetify,
  router,
  store,

  computed: {
    ...mapGetters({
      localStorage_Get: 'localStorage_Get'
    })
  },

  mounted() {
    // Set language from localStorage or DEFAULT
    // Remove commented code when ready
    const language = /* this.localStorage_Get('localization.language') || */ ITS_LOCALIZATION__DEFAULT_LANGUAGE
    store.dispatch(VUEX_LOCALIZATION_CHANGE_LANG, language)
  },

  methods: {
    loadView,
    loadComponent
  },

  watch: {
    '$route.name': () => {
      window.Appcues && window.Appcues.page()
    }
  },
  render: () => h(App)
})

// emitter library
const emitter = mitt()
app.config.globalProperties.emitter = emitter

// Global components
app.component('RecycleScroller', RecycleScroller)

app.component('its-autocomplete', AutoComplete)
app.component('its-combobox', ComboBox)
app.component('its-date-picker', DatePicker)
app.component('its-select', Select)
app.component('its-textarea', TextArea)
app.component('its-text-field', TextField)

// Directives
app.directive('menu-close', MenuClose)
app.directive('uppercase', Uppercase)
app.directive('maska', vMaska)

// Mixins
app.mixin(customValidators)
app.mixin(checkUserPermissions)

// Vue Plugins
app.use(Vuetify)
app.use(VueResize)
app.use(VueScrollTo)
app.use(store)
app.use(router)

i18next(app)

app.config.productionTip = false
app.config.devtools = process.env.VUE_APP_DEV_TOOLS
app.config.performance = process.env.VUE_APP_PERFORMANCE_TOOLS
window.localStorage.debug = process.env.VUE_APP_DEBUG

store
  .dispatch(VUEX_APP_INIT)
  .then(() => {
    app.mount('#app')
  })
  .catch((err) => {
    console.error('src/main.js | VUEX_APP_INIT | Cannot load app', err)
    return err
  })

export default app

export { emitter }
